import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface XeroConnectionStatus {
  connected: false;
  configured: boolean;
}

export interface XeroConnectionStatusConnected {
  connected: true;
  configured: true;
  tenantName: string | null;
  tenantId: string;
  connectedAt: string;
  needsReconnect: boolean;
}

export type XeroStatus = XeroConnectionStatus | XeroConnectionStatusConnected;

export interface XeroSyncLogEntry {
  id: number;
  userId: number;
  source: string;
  jobId: number | null;
  entityType: string;
  entityId: number | null;
  direction: string;
  status: string;
  payloadSummary: string | null;
  errorMessage: string | null;
  durationMs: number | null;
  startedAt: string;
  finishedAt: string | null;
  createdAt: string;
}

export interface XeroSyncStatusResponse {
  entry: XeroSyncLogEntry | null;
  lastSuccess: XeroSyncLogEntry | null;
  externalId: string | null;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export interface XeroBulkSyncStatusEntry {
  externalId: string | null;
  lastSuccessAt: string | null;
  lastFailedAt: string | null;
}

export interface XeroBulkSyncStatusResponse {
  statuses: Record<string, XeroBulkSyncStatusEntry>;
}

export const XERO_STATUS_QUERY_KEY = ["xero", "status"] as const;
export const XERO_SYNC_LOG_QUERY_KEY = ["xero", "syncLog"] as const;
export const xeroInvoiceSyncStatusQueryKey = (invoiceId: number) =>
  ["xero", "invoiceSyncStatus", invoiceId] as const;
export const xeroEstimateSyncStatusQueryKey = (estimateId: number) =>
  ["xero", "estimateSyncStatus", estimateId] as const;
export const XERO_BULK_SYNC_STATUS_QUERY_KEY = ["xero", "bulkSyncStatus"] as const;
export const xeroBulkSyncStatusQueryKey = (
  entityType: "invoice" | "estimate",
  ids: number[],
) =>
  [...XERO_BULK_SYNC_STATUS_QUERY_KEY, entityType, [...ids].sort((a, b) => a - b)] as const;

// ---------------------------------------------------------------------------
// Functions
// ---------------------------------------------------------------------------

export async function getXeroStatus(): Promise<XeroStatus> {
  return customFetch<XeroStatus>("/api/integrations/xero/status");
}

export async function disconnectXero(): Promise<{ ok: boolean }> {
  return customFetch<{ ok: boolean }>("/api/integrations/xero/disconnect", { method: "POST" });
}

export async function getXeroSyncLog(limit = 50): Promise<{ entries: XeroSyncLogEntry[] }> {
  return customFetch<{ entries: XeroSyncLogEntry[] }>(`/api/integrations/xero/sync-log?limit=${limit}`);
}

export async function pushInvoiceToXero(invoiceId: number): Promise<{ ok: boolean; xeroId: string }> {
  return customFetch<{ ok: boolean; xeroId: string }>(`/api/integrations/xero/push-invoice/${invoiceId}`, {
    method: "POST",
  });
}

export async function pushEstimateToXero(estimateId: number): Promise<{ ok: boolean; xeroId: string }> {
  return customFetch<{ ok: boolean; xeroId: string }>(`/api/integrations/xero/push-quote/${estimateId}`, {
    method: "POST",
  });
}

export async function getXeroInvoiceSyncStatus(invoiceId: number): Promise<XeroSyncStatusResponse> {
  return customFetch<XeroSyncStatusResponse>(`/api/integrations/xero/invoice-sync-status/${invoiceId}`);
}

export async function getXeroEstimateSyncStatus(estimateId: number): Promise<XeroSyncStatusResponse> {
  return customFetch<XeroSyncStatusResponse>(`/api/integrations/xero/estimate-sync-status/${estimateId}`);
}

export async function getXeroBulkSyncStatus(
  entityType: "invoice" | "estimate",
  ids: number[],
): Promise<XeroBulkSyncStatusResponse> {
  return customFetch<XeroBulkSyncStatusResponse>(
    "/api/integrations/xero/bulk-sync-status",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entityType, ids }),
    },
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useGetXeroStatus() {
  return useQuery({
    queryKey: XERO_STATUS_QUERY_KEY,
    queryFn: getXeroStatus,
    staleTime: 30_000,
  });
}

export function useDisconnectXero() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectXero,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: XERO_STATUS_QUERY_KEY });
    },
  });
}

export function useGetXeroSyncLog(limit = 50) {
  return useQuery({
    queryKey: [...XERO_SYNC_LOG_QUERY_KEY, limit],
    queryFn: () => getXeroSyncLog(limit),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function usePushInvoiceToXero() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pushInvoiceToXero,
    onSuccess: (_data, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: XERO_SYNC_LOG_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: xeroInvoiceSyncStatusQueryKey(invoiceId) });
      queryClient.invalidateQueries({ queryKey: XERO_BULK_SYNC_STATUS_QUERY_KEY });
    },
    onError: (_err, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: xeroInvoiceSyncStatusQueryKey(invoiceId) });
      queryClient.invalidateQueries({ queryKey: XERO_BULK_SYNC_STATUS_QUERY_KEY });
    },
  });
}

export function usePushEstimateToXero() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pushEstimateToXero,
    onSuccess: (_data, estimateId) => {
      queryClient.invalidateQueries({ queryKey: XERO_SYNC_LOG_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: xeroEstimateSyncStatusQueryKey(estimateId) });
      queryClient.invalidateQueries({ queryKey: XERO_BULK_SYNC_STATUS_QUERY_KEY });
    },
    onError: (_err, estimateId) => {
      queryClient.invalidateQueries({ queryKey: xeroEstimateSyncStatusQueryKey(estimateId) });
      queryClient.invalidateQueries({ queryKey: XERO_BULK_SYNC_STATUS_QUERY_KEY });
    },
  });
}

export function useGetXeroBulkSyncStatus(
  entityType: "invoice" | "estimate",
  ids: number[],
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: xeroBulkSyncStatusQueryKey(entityType, ids),
    queryFn: () => getXeroBulkSyncStatus(entityType, ids),
    enabled: options?.enabled !== false && ids.length > 0,
    staleTime: 10_000,
  });
}

export function useGetXeroInvoiceSyncStatus(invoiceId: number | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: xeroInvoiceSyncStatusQueryKey(invoiceId ?? 0),
    queryFn: () => getXeroInvoiceSyncStatus(invoiceId!),
    enabled: options?.enabled !== false && invoiceId != null,
    staleTime: 10_000,
  });
}

export function useGetXeroEstimateSyncStatus(estimateId: number | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: xeroEstimateSyncStatusQueryKey(estimateId ?? 0),
    queryFn: () => getXeroEstimateSyncStatus(estimateId!),
    enabled: options?.enabled !== false && estimateId != null,
    staleTime: 10_000,
  });
}
